import { API_BASE_URL } from "src/config/apiBase";
import { useEffect, useState } from "react";
import { Phone, Mail, MessageSquare, ReceiptIndianRupee, CheckCircle, ChevronDown } from "lucide-react";
import { PackageCheck, DollarSign, Clock, XCircle } from "lucide-react";
import { motion } from "framer-motion";
import { FiPhone, FiMail, FiInfo } from "react-icons/fi";
import { AiOutlineWhatsApp } from "react-icons/ai";
import { useSidebar } from '../../SidebarContext';
import OrderDetailsModal from "./OrderDetailModal";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { decryptToken } from "../utils/getToken";

const parseAddress = (addrString) => {
    if (!addrString) return null;

    try {
        return typeof addrString === "string"
            ? JSON.parse(addrString)
            : addrString;   // already parsed
    } catch (err) {
        console.error("Address parse error:", err);
        return null;
    }
};


const dateOptions = [
    { label: "Last 7 Days", value: "7" },
    { label: "Last 30 Days", value: "30" },
    { label: "Last 90 Days", value: "90" },
    { label: "Last 6 Months", value: "180" },
    { label: "Last 1 Year", value: "365" },
    { label: "Custom Dates", value: "custom" },
];

export default function CustomerHistory() {
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState("180");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { id } = useParams();
    const url = API_BASE_URL
    const [order, setOrder] = useState([])
    const token = decryptToken("accessToken") || ""
    const currentOrderStatus = "processing";
    const [currentOrders, setCurrentOrders] = useState([]);
    const [user, setUser] = useState({})




    const safeParse = (value) => {
        if (!value) return value;

        try {
            // If it's already an object, return as it is
            if (typeof value !== "string") return value;

            return JSON.parse(value);
        } catch {
            return value; // return original if invalid JSON
        }
    };
    const parseOrder = (order) => {
        const parsed = { ...order };

        for (let key in parsed) {
            parsed[key] = safeParse(parsed[key]);
        }

        // cart_products → array → product_gallery inside each product
        if (Array.isArray(parsed.cart_products)) {
            parsed.cart_products = parsed.cart_products.map((product) => {
                const parsedProduct = { ...product };

                // parse product_gallery if exists
                if (parsedProduct.product_gallery) {
                    parsedProduct.product_gallery = safeParse(parsedProduct.product_gallery);
                }

                return parsedProduct;
            });
        }

        return parsed;
    };

    const normalizeStatus = (s) => {
        if (!s) return "";

        const clean = s.trim().toLowerCase();

        if (clean === "out for delivery" || clean === "out for-delivery" || clean === "outfordelivery") {
            return "Out for delivery";   // match your config key
        }
        if (clean === "delivered") return "Delivered";
        if (clean === "cancelled") return "Cancelled";
        if (clean === "processing") return "Processing";
        if (clean === "pending") return "Pending";
        if (clean === "packed") return "Packed";
        if (clean === "shipped") return "Shipped";

        return s; // fallback
    };


    const ongoingStatuses = [
        "Pending",
        "Processing",
        "Packed",
        "Shipped",
        "Out for delivery"
    ];

    const computeMetrics = (orders) => {
        // Defensive: no orders
        if (!orders || orders.length === 0) {
            return {
                orderCount: 0,
                deliveredValue: 0,
                avgOrderTime: "—",
                canceledCount: 0,
                canceledAmount: 0,
                successRate: "0%"
            };
        }

        const sorted = [...orders].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        const totalOrders = sorted.length;

        // 1️⃣ Delivered Value
        let deliveredValue = sorted
            .filter(o => o.orderStatus === "Delivered")
            .reduce((sum, o) => sum + Number(o.amountafterdiscount || 0), 0);

        // 2️⃣ Cancelled Orders
        const cancelledOrders = sorted.filter(o => o.orderStatus === "Cancelled");
        const cancelledCount = cancelledOrders.length;
        const cancelledAmount = cancelledOrders.reduce(
            (sum, o) => sum + Number(o.amountafterdiscount || 0),
            0
        );

        // 3️⃣ Average Order Time (difference between order dates)
        // 3️⃣ Average Order Time (difference between order dates)
        let avgOrderTime = "—";
        if (sorted.length > 1) {
            let differences = [];

            for (let i = 1; i < sorted.length; i++) {
                const prev = new Date(sorted[i - 1].createdAt);
                const curr = new Date(sorted[i].createdAt);

                const diffMs = curr - prev;
                differences.push(diffMs);
            }

            const avgMs = differences.reduce((a, b) => a + b, 0) / differences.length;

            const avgDays = Math.floor(avgMs / (1000 * 60 * 60 * 24));
            const avgHours = Math.floor((avgMs / (1000 * 60 * 60)) % 24);
            const avgMinutes = Math.floor((avgMs / (1000 * 60)) % 60);

            avgOrderTime = `${avgDays}d ${avgHours}h ${avgMinutes}m`;
        }


        // 5️⃣ Success Rate
        const deliveredCount = sorted.filter(o => o.orderStatus === "Delivered").length;
        const successRate = Math.round((deliveredCount / totalOrders) * 100) + "%";

        return {
            orderCount: totalOrders,
            deliveredValue,
            avgOrderTime,
            cancelledCount,
            cancelledAmount,
            successRate
        };
    };


    const fetchUserData = async () => {
        try {
            const response = await fetch(`${url}/registeruser/single/${id}`)
            const res = await response.json();
            console.log(res);
            setUser(res);
        } catch (error) {
            console.error(error)
        }
    }

    const fetchOrderData = async () => {
        try {
            const response = await fetch(`${url}/payment/${id}`);
            const res = await response.json();

            const data = res.data;

            const parsedOrders = data.map((order) => parseOrder(order));
            const ongoingOrders = parsedOrders.filter(order =>
                ongoingStatuses.includes(order.orderStatus)
            );

            setCurrentOrders(ongoingOrders); // MULTIPLE
            setOrder(parsedOrders);

        } catch (error) {
            console.error(error);
        }
    };




    const handleStatusChange = async (newStatus, lead) => {
        if (!newStatus || !String(newStatus).trim()) return;

        try {
            const statusDateMap = {
                "Delivered": "delivery_date",
                "Processing": "processing_date",
                "Packed": "packed_date",
                "Shipped": "shipped_date",
                "Out for delivery": "out_deliverydate"
            };

            const dateField = statusDateMap[newStatus];
            const now = new Date().toISOString();

            const body = {
                orderStatus: newStatus,
                ...(dateField && { [dateField]: now })
            };

            const response = await fetch(`${url}/payment/${lead.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body)
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok && result?.success !== false && !result?.isError) {
                toast.success("Status updated successfully");

                setOrder(prev =>
                    prev.map(item =>
                        item.id === lead.id
                            ? { ...item, orderStatus: newStatus, ...(dateField && { [dateField]: now }) }
                            : item
                    )
                );
                setCurrentOrders(prev =>
                    prev.map(item =>
                        item.id === lead.id
                            ? { ...item, orderStatus: newStatus, ...(dateField && { [dateField]: now }) }
                            : item
                    )
                );
            } else {
                toast.error(result.message || "Failed to update status");
            }
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("An error occurred while updating status");
        }
        // finally {
        //     setIsUpdatingStatus(false);
        // }
    };

    useEffect(() => {
        fetchOrderData();
        fetchUserData();
    }, [])






    const filterByDate = (orders) => {
        if (selected === "all") return orders;

        const now = new Date();

        // Predefined ranges
        if (selected !== "custom") {
            const days = Number(selected);
            const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

            return orders.filter(o => new Date(o.createdAt) >= cutoff);
        }

        // Custom Range
        if (selected === "custom" && startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            return orders.filter(o => {
                const d = new Date(o.createdAt);
                return d >= start && d <= end;
            });
        }

        return orders;
    };


    // 1. Status filter
    // MASTER FILTER PIPELINE
    const dateFilteredOrders = filterByDate(order);

    const statusFilteredOrders =
        filter === "all"
            ? dateFilteredOrders
            : dateFilteredOrders.filter((o) => o.orderStatus === filter);

    // this drives EVERYTHING else
    const filteredOrders = statusFilteredOrders;


    const metrics = computeMetrics(filteredOrders);

    const currentOrdersFiltered = filteredOrders.filter(o =>
        ongoingStatuses.includes(o.orderStatus)
    );

    const filteredHistory = filteredOrders;


    const customerMetrics = [
        {
            label: "Order Count",
            value: metrics.orderCount,
            icon: PackageCheck,
            bg: "from-purple-300/20 to-blue-300/20",
            iconBg: "from-purple-500 to-blue-500",
        },
        {
            label: "Delivered Value",
            value: `₹${metrics.deliveredValue.toLocaleString()}`,
            icon: ReceiptIndianRupee,
            bg: "from-emerald-300/20 to-green-300/20",
            iconBg: "from-emerald-500 to-green-600",
        },
        {
            label: "Avg. Order Time",
            value: metrics.avgOrderTime,
            icon: Clock,
            bg: "from-yellow-200/20 to-orange-200/20",
            iconBg: "from-yellow-500 to-orange-500",
        },
        {
            label: "Canceled Orders",
            value: `${metrics?.cancelledCount || 0} (₹${metrics?.cancelledAmount || 0})`,
            icon: XCircle,
            bg: "from-red-300/20 to-pink-300/20",
            iconBg: "from-red-500 to-pink-500",
        },
        {
            label: "Success Rate",
            value: metrics.successRate,
            icon: CheckCircle,
            bg: "from-blue-300/20 to-teal-300/20",
            iconBg: "from-blue-600 to-teal-500",
        },
    ];

    const getStatusConfig = (status) => {
        const config = {
            "Pending": { color: "bg-yellow-100 text-yellow-700 border-yellow-200", progress: "10%", bar: "bg-yellow-500" },
            "Processing": { color: "bg-blue-100 text-blue-700 border-blue-200", progress: "25%", bar: "bg-blue-500" },
            "Packed": { color: "bg-indigo-100 text-indigo-700 border-indigo-200", progress: "45%", bar: "bg-indigo-500" },
            "Shipped": { color: "bg-purple-100 text-purple-700 border-purple-200", progress: "65%", bar: "bg-purple-500" },
            "Out for delivery": { color: "bg-orange-100 text-orange-700 border-orange-200", progress: "85%", bar: "bg-orange-500" },
            "Delivered": { color: "bg-green-100 text-green-700 border-green-200", progress: "100%", bar: "bg-green-500" },
            "Cancelled": { color: "bg-red-100 text-red-700 border-red-200", progress: "100%", bar: "bg-red-400 opacity-60" }
        };

        return config[status] || { color: "bg-gray-100 text-gray-700 border-gray-200", progress: "0%", bar: "bg-gray-400" };
    };

    const customerPhone = user?.mobile || "";
    const customerWhatsApp = user?.mobile || "";
    const customerEmail = user?.email || "";


    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    return (
        <>
            <div className="px-1 space-y-4 -mt-2 pb-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 gap-3">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-900">
                            {user.full_name}
                        </h2>
                        <p className="text-xs text-gray-500">
                            {user.mobile} · {user.email}
                        </p>
                    </div>

                    <div className="flex flex-row gap-2 md:items-center">
                        <div className="relative w-fit">
                            <select
                                value={selected}
                                onChange={(e) => setSelected(e.target.value)}
                                className="appearance-none pr-8 pl-3 py-2 rounded-lg border border-gray-300 bg-white text-sm shadow-sm focus:ring-1 focus:ring-blue-500 cursor-pointer outline-0"
                            >
                                {dateOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>

                            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        </div>

                        {selected === "custom" && (
                            <Flatpickr
                                options={{
                                    mode: "range",
                                    dateFormat: "Y-m-d",
                                }}
                                value={[startDate, endDate]}
                                onChange={(dates) => {
                                    setStartDate(dates[0] || "");
                                    setEndDate(dates[1] || "");
                                }}
                                className="px-3 py-2 text-sm border rounded-lg shadow-sm bg-white cursor-pointer outline-none w-[200px]"
                                placeholder="Select date range"
                            />
                        )}
                    </div>
                </div>
                {/* Customer Metrics */}
                <div className="flex overflow-x-auto no-scrollbar gap-4">
                    {customerMetrics.map((item, index) => (
                        <div
                            key={index}
                            className={`flex shrink-0 min-w-160 items-center gap-3 p-3 rounded-xl bg-gradient-to-br ${item.bg} backdrop-blur-md border border-white/40 shadow-sm`}
                        >
                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.iconBg} text-white flex items-center justify-center shadow-md`}>
                                <item.icon className="w-5 h-5" />
                            </div>

                            <div>
                                <p className="text-lg md:text-xl font-bold text-gray-900">
                                    {item.value}
                                </p>
                                <p className="text-[10px] sm:text-xs text-gray-600 font-medium">
                                    {item.label}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>


                {/* Contact Section */}
                <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900">Contact Options</h3>

                    <div className="flex flex-wrap gap-2">

                        {/* CALL */}
                        <button
                            onClick={() => {
                                if (!customerPhone) return toast.error("No phone number available");
                                window.location.href = `tel:${customerPhone}`;
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white text-gray-700 text-sm shadow hover:shadow-md hover:scale-105 transition"
                        >
                            <FiPhone size={15} className="text-blue-600" />
                            Call
                        </button>

                        {/* WHATSAPP */}
                        <button
                            onClick={() => {
                                if (!customerWhatsApp) return toast.error("No WhatsApp number available");
                                window.open(`https://wa.me/${customerWhatsApp}`, "_blank");
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white text-gray-700 text-sm shadow hover:shadow-md hover:scale-105 transition"
                        >
                            <AiOutlineWhatsApp size={16} className="text-green-600" />
                            WhatsApp
                        </button>

                        {/* EMAIL */}
                        <button
                            onClick={() => {
                                if (!customerEmail) return toast.error("No email available");
                                window.location.href = `mailto:${customerEmail}`;
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white text-gray-700 text-sm shadow hover:shadow-md hover:scale-105 transition"
                        >
                            <FiMail size={15} className="text-red-500" />
                            Email
                        </button>

                        {/* INFO (optional navigation) */}
                        <button
                            onClick={() => {
                                console.log("Open customer info modal/page");
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white text-gray-700 text-sm shadow hover:shadow-md hover:scale-105 transition"
                        >
                            <FiInfo size={15} className="text-purple-600" />
                            Info
                        </button>

                    </div>
                </div>





                {currentOrdersFiltered.length > 0 ? (
                    <div className="space-y-4">
                        {currentOrdersFiltered.map((order, idx) => {
                            const normalized = normalizeStatus(order.orderStatus);
                            const config = getStatusConfig(normalized);


                            return (
                                <motion.div
                                    key={order.id}
                                    initial={{ opacity: 0.9 }}
                                    animate={{ opacity: [1, 0.9, 1] }}
                                    transition={{ duration: 1.8, repeat: Infinity }}
                                    className="relative p-4 rounded-xl border border-blue-300/50 bg-gradient-to-br from-blue-50 to-white shadow-md backdrop-blur-sm overflow-hidden"
                                >
                                    <motion.div
                                        className="absolute inset-0 rounded-xl border-2 border-blue-400 opacity-20"
                                        animate={{ opacity: [0.2, 0.6, 0.2] }}
                                        transition={{ repeat: Infinity, duration: 2 }}
                                    />

                                    <p className="text-xs font-bold text-blue-700 tracking-wide uppercase">
                                        Current Order
                                    </p>

                                    <div className="flex items-center gap-2 mt-1">
                                        <p className="text-base font-extrabold text-gray-900">
                                            #{order.receipt_id || order.order_id}
                                        </p>

                                        <span className={`px-2 py-0.5 text-xs font-semibold text-white rounded-full ${config.color}`}>
                                            {normalized}
                                        </span>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-3">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: config.progress }}
                                            transition={{ duration: 0.7, ease: "easeOut" }}
                                            className={`h-full rounded-full ${config.bar}`}
                                        />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm">No Active Orders</p>
                )}

                {/* Filter */}
                <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm font-medium text-gray-700">Filter by Status:</label>

                    <div className="relative">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="appearance-none pr-8 pl-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-40 cursor-pointer"
                        >
                            <option value="all">All Orders</option>
                            <option value="Pending">Pending</option>
                            <option value="Processing">Processing</option>
                            <option value="Packed">Packed</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Out for delivery">Out for Delivery</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>

                        {/* Custom Chevron */}
                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <svg
                                className="w-4 h-4 text-gray-600 transition-transform duration-300 peer-focus:rotate-180"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </span>
                    </div>
                </div>

                {/* Order History - All Status Cards */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Order History ({filteredHistory.length})</h3>
                    {filteredHistory.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                            <p className="text-gray-500">No orders match the selected filter.</p>
                        </div>
                    ) : (
                        filteredHistory.map((order, idx) => {
                            const config = getStatusConfig(order.status);
                            return (
                                <motion.div
                                    key={order.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: idx * 0.08 }}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 hover:shadow-md transition-all"
                                >
                                    {/* Header */}
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="text-base font-bold text-gray-900">Order ID: {order.receipt_id}</p>
                                            <p className="text-xs text-gray-500">
                                                {order.order_id} · {new Date(order.createdAt).toLocaleString("en-GB")}
                                            </p>
                                            <OrderDetailsModal order={order} />
                                        </div>
                                        <div className="relative w-max">
                                            <select
                                                name="status"
                                                value={order.orderStatus || ""}
                                                onChange={(e) => handleStatusChange(e.target.value, order)}
                                                className="appearance-none bg-transparent tracking-wide border border-gray-300 text-sm px-3 py-1.5 pr-7 rounded-md cursor-pointer focus:border-gray-400 focus:outline-none transition"
                                            >
                                                <option value="">Select</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Processing">Processing</option>
                                                <option value="Packed">Packed</option>
                                                <option value="Shipped">Shipped</option>
                                                <option value="Out for delivery">Out for delivery</option>
                                                <option value="Delivered">Delivered</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>

                                            <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        </div>
                                        {/* <span
                                            className={`px-3 py-1 text-xs font-bold rounded-full border ${config.color}`}
                                        >
                                            {capitalize(order.status)}
                                        </span> */}
                                    </div>

                                    {/* Items */}
                                    {/* <p className="text-sm text-gray-700 mb-2">
                                        <span className="font-medium text-gray-900">Item:</span> {order.items}
                                    </p> */}
                                    <div className="mb-3">
                                        <p className="text-sm font-medium text-gray-900">Items:</p>

                                        {order?.cart_products.length === 0 ? (
                                            <p className="text-xs text-gray-500">No items found</p>
                                        ) : (
                                            <div className="mt-1 space-y-2">
                                                {order?.cart_products.map((item, index) => (
                                                    <div key={index} className="flex items-center gap-3 border p-2 rounded-lg bg-gray-50">

                                                        {/* Product Image */}
                                                        <img
                                                            src={`${url}${item.product_image}`}
                                                            alt={item.product_name}
                                                            className="w-12 h-12 rounded-md object-cover"
                                                        />

                                                        {/* Item Details */}
                                                        <div className="flex flex-col">
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {item.product_name}
                                                            </p>

                                                            <p className="text-xs text-gray-600">
                                                                SKU: {item.product_sku}
                                                            </p>

                                                            <p className="text-xs text-gray-700">
                                                                Qty: {item.qty || 1}
                                                            </p>

                                                            {/* Price */}
                                                            <p className="text-sm font-bold text-gray-900">
                                                                ₹{item.product_saleprice}
                                                            </p>
                                                        </div>

                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>


                                    {/* Amount */}
                                    <p className="text-base font-bold text-gray-900 mb-3">
                                        ₹{order.amountafterdiscount}
                                    </p>

                                    {order?.cancel_reason && (
                                        <div className="mt-2 mb-2 rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-700">
                                            <strong>Order Cancelled:</strong> {order.cancel_reason}
                                        </div>
                                    )}


                                    {/* Addresses */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                                        {/* <div>
                                            <p className="font-medium text-gray-700">Delivery Address</p>
                                            <p className="text-gray-600 text-xs">12 Skyline Ave, Andheri West, Mumbai - 400058</p>
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-700">Billing Address</p>
                                            <p className="text-gray-600 text-xs">Same as delivery</p>
                                        </div> */}
                                        <div>
                                            <p className="font-medium text-gray-700">Delivery Address</p>

                                            {order?.shipping_address ? (
                                                <p className="text-gray-600 text-xs leading-5">
                                                    {order?.shipping_address.new_address && `${order?.shipping_address.new_address}, `}
                                                    {order?.shipping_address.area && `${order?.shipping_address.area}, `}
                                                    {order?.shipping_address.city && `${order?.shipping_address.city}, `}
                                                    {order?.shipping_address.pincode && `Pincode: ${order?.shipping_address.pincode}`}
                                                </p>
                                            ) : (
                                                <p className="text-gray-500 text-xs">No address found</p>
                                            )}
                                        </div>

                                        {/* Billing Address */}
                                        <div>
                                            <p className="font-medium text-gray-700">Billing Address</p>

                                            {order?.billing_address ? (
                                                <p className="text-gray-600 text-xs leading-5">
                                                    {order?.billing_address.new_address && `${order?.billing_address.new_address}, `}
                                                    {order?.billing_address.area && `${order?.billing_address.area}, `}
                                                    {order?.billing_address.city && `${order?.billing_address.city}, `}
                                                    {order?.billing_address.pincode && `Pincode: ${order?.billing_address.pincode}`}
                                                </p>
                                            ) : (
                                                <p className="text-gray-500 text-xs">No address found</p>
                                            )}
                                        </div>


                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: config.progress }}
                                            transition={{ duration: 0.7, ease: "easeOut" }}
                                            className={`h-full rounded-full ${config.bar}`}
                                        />
                                    </div>
                                </motion.div>
                            );
                        })
                    )}
                </div>
            </div>
        </>
    );
}
